import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}
export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-292">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-293">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-bio-black/90 backdrop-blur-sm" data-id="element-294" />

          {/* Modal */}
          <motion.div initial={{
        scale: 0.95,
        y: 20,
        opacity: 0
      }} animate={{
        scale: 1,
        y: 0,
        opacity: 1
      }} exit={{
        scale: 0.95,
        y: 20,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative bg-bio-dark border border-bio-gray/60 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-295">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-bio-teal" data-id="element-296" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-bio-teal" data-id="element-297" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-bio-teal" data-id="element-298" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-bio-teal" data-id="element-299" />

            {/* Header */}
            <div className="flex items-start justify-between p-6 md:p-8 pb-4 border-b border-bio-gray/40" data-id="element-300">
              <div data-id="element-301">
                {subtitle && <div className="font-mono text-xs text-bio-teal tracking-widest mb-2 uppercase" data-id="element-302">
                    {subtitle}
                  </div>}
                <h2 id="modal-title" className="font-oswald text-2xl md:text-3xl font-bold text-white uppercase tracking-wide" data-id="element-303">
                  {title}
                </h2>
              </div>
              <button onClick={onClose} aria-label="Close" className="text-bio-muted hover:text-bio-teal transition-colors shrink-0 ml-4 p-1" data-id="element-304">
                <X className="w-6 h-6" data-id="element-305" />
              </button>
            </div>

            {/* Content - scrollable */}
            <div className="overflow-y-auto px-6 md:px-8 py-6 text-bio-text font-light text-sm leading-relaxed space-y-4 [&_h3]:font-oswald [&_h3]:text-white [&_h3]:text-base [&_h3]:tracking-wide [&_h3]:mt-6 [&_h3]:mb-2 [&_strong]:text-white [&_strong]:font-normal" data-id="element-306">
              {children}
            </div>

            {/* Footer mono label */}
            <div className="px-6 md:px-8 py-3 border-t border-bio-gray/40 font-mono text-[10px] text-bio-muted/50 tracking-widest flex justify-between" data-id="element-307">
              <span data-id="element-308">SOMIBO HEALING // CIVILIAN PROTOCOL</span>
              <span data-id="element-309">v1.0</span>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}